'use client';

import routes from 'src/routes';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Button } from '@trussworks/react-uswds';

import { useAwsRum } from 'src/components/ClientSideMonitoring/clientSideMonitoring';
import CustomLink from 'src/components/CustomLink';

export default function Error500s({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const rum = useAwsRum();
  const t = useTranslations('error');

  useEffect(() => {
    // Log error for reporting
    console.error(error);
    if (rum) {
      rum.recordError(error);
    }
  }, [rum, error]);

  // Extract a 3-digit error code from error message if available, fallback to 500
  const match = error.message.match(/[45]\d{2}/);
  const code = match ? match[0] : '500';

  return (
    <main className="usa-section usa-prose">
      <div className="grid-container">
        <h1 className="usa-display">{t(`${code}.title`)}</h1>

        <p className="usa-intro text-lg">{t(`${code}.description`)}</p>

        <p className="text-sm">
          {t.rich(`${code}.guidance`, {
            telephone_link: () => (
              <CustomLink href={`tel:${routes.external.phones.mass_211_contact_number}`}>
                {routes.external.phones.mass_211_contact_number}
              </CustomLink>
            ),
          })}
        </p>
        <p className="text-sm">
          {t.rich(`${code}.support`, {
            telephone_link: () => (
              <CustomLink href={`tel:${routes.external.phones.mass_211_contact_number}`}>
                {routes.external.phones.mass_211_contact_number}
              </CustomLink>
            ),
          })}
        </p>

        <div className="margin-top-4">
          {/* Use window.location instead of router to avoid breaking the error boundary */}
          <Button type="button" onClick={() => reset()}>
            {t(`button.try_again`)}
          </Button>
          {/* eslint-disable-next-line @next/next/no-location-assign-relative-destination */}
          <Button type="button" onClick={() => window.location.assign(`${process.env.NEXT_PUBLIC_BASE_PATH}/home`)}>
            {t(`button.home`)}
          </Button>
        </div>

        <div className="margin-top-4">
          <p>{t('contact.assistance')}</p>
          <ul>
            <li>
              {t.rich('contact.call', {
                link: () => (
                  <CustomLink href={`tel:${routes.external.phones.mass_211_contact_number}`}>
                    {routes.external.phones.mass_211_contact_number}
                  </CustomLink>
                ),
              })}
            </li>
          </ul>
        </div>

        <p className="margin-top-4 text-base">{t('error_code', { code: code })}</p>
      </div>
    </main>
  );
}
